
import { ReactNode } from 'react';
import { Button } from "@/components/ui/button";
import { Home, Target, Sword, User, Briefcase, RotateCcw } from 'lucide-react';
import NotificationSystem from './components/NotificationSystem';
import type { MiniAppView } from './types';

interface MiniAppLayoutProps {
  children: ReactNode;
  currentView: MiniAppView;
  onNavigate: (view: MiniAppView) => void;
  user?: { id: string; brocode: string; } | null;
}

const MiniAppLayout = ({ children, currentView, onNavigate, user }: MiniAppLayoutProps) => {
  console.log('🎨 MiniAppLayout rendering with view:', currentView);
  
  // Don't show navigation for initiation view
  if (currentView === 'initiation') {
    return (
      <div 
        className="min-h-screen bg-navy text-tan"
        style={{ 
          backgroundColor: '#1a1f36',
          color: '#e2c044',
          minHeight: '100vh'
        }}
      >
        <div className="container mx-auto px-4 py-6">
          <div className="text-center mb-6">
            <h1 
              className="text-xl font-bold text-tan"
              style={{ color: '#e2c044' }}
            >
              BroRankBot
            </h1>
            <p 
              className="text-sm text-tan-dark"
              style={{ color: '#c29c3a' }}
            >
              Complete Your Initiation
            </p>
          </div>
          {children}
        </div>
      </div>
    );
  }

  const navigationItems: { view: MiniAppView; icon: any; label: string }[] = [
    { view: 'dashboard', icon: Home, label: 'Home' },
    { view: 'engagement', icon: Target, label: 'Missions' },
    { view: 'jobs', icon: Briefcase, label: 'Jobs' },
    { view: 'wagers', icon: Sword, label: 'Wagers' },
    { view: 'roulette', icon: RotateCcw, label: 'Roulette' },
    { view: 'profile', icon: User, label: 'Profile' },
  ];

  return (
    <div 
      className="min-h-screen bg-background text-foreground flex flex-col"
      style={{ 
        backgroundColor: '#1a1f36',
        color: '#e2c044',
        minHeight: '100vh'
      }}
    >
      {/* Header */}
      <div 
        className="bg-secondary p-4 border-b border-border"
        style={{ 
          backgroundColor: '#2d3748',
          borderColor: '#4a5568'
        }}
      >
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <h1 
              className="text-xl font-bold text-foreground"
              style={{ color: '#e2c044' }}
            >
              BroRankBot
            </h1>
            <p 
              className="text-sm text-muted-foreground"
              style={{ color: '#9ca3af' }}
            >
              BroMentum Mini-App
            </p>
          </div>
          {user && (
            <NotificationSystem 
              userId={user.id} 
              onNotificationClick={(notification) => {
                // Handle notification click - could navigate to relevant view
                console.log('Notification clicked:', notification);
              }}
            />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {children}
      </div>

      {/* Bottom Navigation */}
      <div 
        className="bg-secondary border-t border-border p-2 safe-area-bottom"
        style={{ 
          backgroundColor: '#2d3748',
          borderColor: '#4a5568'
        }}
      >
        <div className="grid grid-cols-5 gap-1">
          {navigationItems.map(({ view, icon: Icon, label }) => (
            <Button
              key={view}
              variant={currentView === view ? "default" : "ghost"}
              size="sm"
              onClick={() => onNavigate(view)}
              className={`flex flex-col items-center p-2 h-auto transition-all duration-200 ${
                currentView === view 
                  ? 'bg-primary text-primary-foreground shadow-md' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              style={currentView === view ? {
                backgroundColor: '#e2c044',
                color: '#1a1f36'
              } : {
                color: '#9ca3af'
              }}
            >
              <Icon size={16} />
              <span className="text-xs mt-1">{label}</span>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MiniAppLayout;
